import { Link } from "react-router-dom";
import { gossipTestimonials } from "@/mocks/gossipTestimonials";
import { gossipCategories } from "@/mocks/gossipCategories";
import Seo from "@/components/base/Seo";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { SITE_EMAIL, SITE_URL } from "@/utils/site";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "定位",
    desc: "不做爆料源头，只做信息整理。把公开可见的进展按时间排清楚，让读者自己判断。",
  },
  {
    icon: "ri-compass-3-line",
    title: "理念",
    desc: "热闹可以有，但不必喧哗。我们相信把事实讲清楚，比制造情绪更有长期价值。",
  },
  {
    icon: "ri-scales-3-line",
    title: "原则",
    desc: "信源分层、标注待确认、有误即改。三条底线贯穿每一条内容的编辑流程。",
  },
];

const metrics = [
  { value: "12 万+", label: "每日时间线浏览" },
  { value: "30 分钟", label: "巡检间隔" },
  { value: "5 大", label: "内容栏目" },
  { value: "2 个工作日", label: "邮件回复时效" },
];

export default function AboutPage() {
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_URL}/about` },
    ],
  };

  const aboutLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于瓜料动态",
    url: `${SITE_URL}/about`,
    inLanguage: "zh-CN",
    mainEntity: {
      "@type": "Organization",
      name: "瓜料动态",
      url: SITE_URL,
      description: "娱乐吃瓜资讯整理平台，提供今日更新、实时吃瓜与最新进展梳理。",
      email: SITE_EMAIL,
    },
  };

  return (
    <>
      <Seo
        title="关于我们 | 瓜料动态"
        description="了解瓜料动态的品牌定位、发展理念与内容原则：以整理代替炒作，以时间线代替情绪，持续提供实时吃瓜与最新进展。"
        keywords="瓜料动态关于我们,瓜料动态内容原则,瓜料动态品牌介绍"
        path="/about"
        jsonLd={[breadcrumbLd, aboutLd]}
      />
      <Layout>
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20abstract%20studio%20interior%20with%20soft%20amber%20light%20rays%20and%20curtain%20shadows%2C%20stylized%20editorial%20atmosphere%2C%20no%20people%2C%20crimson%20and%20cream%20tones&width=1600&height=700&seq=gua-about-hero-01&orientation=landscape"
              alt="瓜料动态品牌介绍配图"
              title="瓜料动态 - 关于我们"
              className="h-full w-full object-cover object-top opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/90 to-foreground-950/60" />
          </div>
          <div className="relative mx-auto max-w-shell px-4 py-14 md:px-6 md:py-20">
            <div className="[&_a]:text-background-300 [&_span]:text-background-100">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            </div>
            <h1 className="mt-6 font-heading text-3xl font-bold text-background-50 md:text-4xl">
              关于瓜料动态
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              瓜料动态整理娱乐话题的公开进展，用时间线代替碎片化的消息。我们相信，一个人不需要追着几十个账号跑，也能在多看两眼之后搞清楚事情走到了哪一步。
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-shell px-4 py-14 md:px-6 md:py-16">
          <div className="grid gap-5 md:grid-cols-3">
            {positioning.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-2 gap-5 rounded-lg border border-background-200 bg-background-100 p-6 md:grid-cols-4 md:p-8">
            {metrics.map((item) => (
              <div key={item.label}>
                <p className="font-heading text-2xl font-bold text-primary-600 md:text-3xl">{item.value}</p>
                <p className="mt-1.5 text-sm text-foreground-600">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-14 md:py-16">
          <div className="mx-auto max-w-shell px-4 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容是怎么做出来的</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              一条内容从发现到发布，会经过筛选、信源确认和成文整理三个环节。任何一步没有通过，就不会出现在时间线上。
            </p>
            <ol className="mt-8 grid gap-5 md:grid-cols-3">
              {[
                { step: "01", title: "发现与筛选", desc: "盯住全网讨论度上升的话题，先判断它是否具备可整理的信息量。" },
                { step: "02", title: "信源确认", desc: "核对原始出处，区分官方发布、媒体报道与待确认说法，并分别标注。" },
                { step: "03", title: "成文与更新", desc: "按时间顺序梳理背景与进展，形成可回看的页面，后续有新信息继续补充。" },
              ].map((item) => (
                <li key={item.step} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="font-heading text-2xl font-bold text-primary-300">{item.step}</span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto max-w-shell px-4 py-14 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者口碑</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {gossipTestimonials.slice(0, 3).map((item) => (
              <figure key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-300" />
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">{item.quote}</blockquote>
                <figcaption className="mt-5 border-t border-background-200 pt-4">
                  <span className="block font-label text-sm font-medium text-foreground-900">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-shell px-4 pb-16 md:px-6">
          <div className="grid gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                无论是提供线索、指出内容错误，还是希望转载与合作，都欢迎通过下面的方式联系。我们不接受付费删稿，也不会因商业原因调整已经发布的事实陈述。
              </p>
              <a
                href={`mailto:${SITE_EMAIL}`}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-line" /> {SITE_EMAIL}
              </a>
            </div>
            <div className="rounded-lg bg-background-100 p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">可以从这些栏目开始</h3>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {gossipCategories.map((category) => (
                  <li key={category.slug}>
                    <Link
                      to={`/category/${category.slug}`}
                      className="flex items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      {category.name}
                      <i className="ri-arrow-right-s-line text-foreground-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
}